<template>
    <div
        class="synchronized-well-video well-video-container"
        :class="{ scaled: isScaled }"
        @mouseleave="isScaled = false"
    >
        <b-spinner v-if="!localVideoPlaybackReady" class="video-spinner" variant="primary" />
        <video
            :id="playerId"
            class="well-view-video rounded-lg"
            :controls="false"
            style="width: 100%; height: 100%"
            :style="`-webkit-filter: brightness(${brightness}) invert(${invert ? '1' : '0'}); `"
            preload="auto"
            playsinline
        >
            <source ref="playerSource" :src="videoURL" type="video/mp4" />
        </video>

        <well-status-overlay :well="well" />

        <div v-if="showNA" class="event-unavailable-overlay">
            <span>{{ $translate('na') }}</span>
        </div>

        <div ref="flash_overlay" class="flash-overlay un-flash darker"></div>

        <div class="magnifying-area" @mouseenter="isScaled = true">
            <img src="/static/svg/magnify.svg" alt="" />
        </div>

        <well-no-embryo-overlay v-if="!well.runs" class="w-100 h-100 rounded-top-lg" />

<!--        <well-graphical-events-bar-->
<!--            v-if="!isFdaFriendly"-->
<!--            :slide-incubation-length="slideIncubationLength"-->
<!--            :well="well"-->
<!--        />-->

        <well-info :well="well" />

        <well-time-view
            :minimal="true"
            :current-well-time_h="currentWellTime_h"
            :show-na="showFertNa"
        />
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {
    INSTRUMENT_TYPE,
    TREATMENT_PAGE_VIEWS,
    TREATMENT_STATUS,
    VIDEO_SEEK_MODES
} from '../../business/const'
import Utils from '../../business/utils/Utils'
import WellTimeView from './common/WellTimeView'
import WellGraphicalEventsBar from './common/WellGraphicalEventsBar'
import WellNoEmbryoOverlay from './WellNoEmbryoOverlay'
import WellStatusOverlay from './common/WellStatusOverlay'
import WellInfo from './WellInfo'

export default {
    name: 'SynchronizedWellVideo',
    components: {
        WellInfo,
        WellStatusOverlay,
        WellNoEmbryoOverlay,
        WellGraphicalEventsBar,
        WellTimeView
    },
    props: {
        well: {
            type: Object,
            required: true
        },
        quality: {
            type: String,
            required: true
        },
        master: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            player: null,
            timecode: 0, // equals to player.currentTime to bind computed
            isPlayerTimeupdateEventListenerSet: false,
            localVideoPlaybackReady: false,
            mkEventUnavailable: false,
            prevEvent: null,
            isScaled: false,
            VIDEO_SEEK_MODES
        }
    },
    computed: {
        ...mapState({
            videoPlaybackReady: state => state.timelapse.videoPlaybackReady,
            invert: state => state.video.invert,
            playState: state => state.video.playState,
            brightness: state => state.video.brightness,
            videoSeekMode: state => state.video.videoSeekMode,
            currentTimeCode_s: state => state.video.currentTimeCode_s,
            selectedMKEventSN: state => state.video.selectedMKEventSN,
            selectedIstanbulEventSN: state => state.video.selectedIstanbulEventSN,
            selectedDaySN: state => state.video.selectedDaySN,
            currentView: state => state.navigation.currentView,
            selectedIstanbulEvent: state => state.video.selectedIstanbulEvent,
            isFertilizationCheck: state => state.video.isFertilizationCheck,
            seekBar_currentTimeCode_s: state => state.video.seekBar_currentTimeCode_s,
            selectedTreatment: state => state.timelapse.selectedTreatment
        }),
        ...mapGetters({
            isFdaFriendly: 'common/isFdaFriendly',
            availableFocals: 'timelapse/availableFocals',
            slideIncubationLength: 'video/slideIncubationLength',
            videoLength_s: 'video/videoLength_s',
            numberOfRuns: 'video/numberOfRuns',
            fpsCalibrationTime_s: 'video/fpsCalibrationTime_s',
            getPnCount: 'timelapse/getPnCount',
            getPnCountTime: 'timelapse/getPnCountTime'
        }),
        playerId() {
            return `sync_video_${this.well._id}`
        },
        videoURL() {
            const sharpestFocal = 0

            const { videoUrl, videoLength } = this.well.videos[this.quality].find(
                _video => _video.focal === sharpestFocal
            )

            // initializes video in iPhone & iPad + thumbnail
            return `${videoUrl}#t=${videoLength}`
        },
        currentRunIndex() {
            return Utils.calculateRunIndex(this.timecode, this.numberOfRuns, this.videoLength_s)
        },
        currentRun() {
            return this.well.runs[this.currentRunIndex]
        },
        currentWellTime_h() {
            return this.currentRun ? this.currentRun.time : 0
        },
        pnCount() {
            return this.getPnCount(this.well)
        },
        pnCountTime() {
            return this.getPnCountTime(this.well)
        },
        showNA() {
            return this.videoSeekMode === VIDEO_SEEK_MODES.MK_EVENT && this.mkEventUnavailable
        },
        showFertNa() {
            return this.isFertilizationCheck && !(this.pnCountTime && this.pnCount)
        }
    },
    watch: {
        well(newVal) {
            if (newVal) this.setPlayerListeners()
        },
        videoPlaybackReady(isReady) {
            if (isReady) {
                this.addPlayerTimeupdateEventListener()
            }
        },
        seekBar_currentTimeCode_s() {
            if (this.playState === 'play' && this.currentRun) {
                const { mkEvent } = this.currentRun

                if (mkEvent && this.prevEvent !== mkEvent) {
                    this.$uiUtils.flashElement(this.$refs.flash_overlay, 0, 200)

                    this.prevEvent = mkEvent
                }
            }
        },
        currentTimeCode_s() {
            this.updatePlayerCurrentTime()
        },
        playState() {
            this.prevEvent = null

            if (this.playState === 'play' && this.videoPlaybackReady) {
                this.player.play()
            } else {
                this.player.pause()
            }
        },
        selectedMKEventSN(newEvent) {
            if (!newEvent) {
                return
            }
            const { inferenceData, runs } = this.well

            const targetMkEvent = inferenceData.morphokinetics.find(mkEvent => {
                return newEvent && newEvent.toLowerCase() === mkEvent.systemName.toLowerCase()
            })

            let targetMkEventRunIndex = null

            for (const runKey in runs) {
                const run = runs[runKey]

                if (
                    run.mkEvent &&
                    (newEvent === null ||
                        (targetMkEvent && run.mkEvent.value === targetMkEvent.value))
                ) {
                    targetMkEventRunIndex = parseInt(runKey)
                    break
                }
            }

            if (targetMkEventRunIndex >= 0) {
                this.mkEventUnavailable = false
                this.$store.dispatch('video/skipToFrame', {
                    frame: targetMkEventRunIndex,
                    player: this.player
                })
            } else {
                this.mkEventUnavailable = true
            }
        },
        selectedIstanbulEventSN(newEvent) {
            if (!newEvent) {
                return
            }
            const { runIndex } = Utils.getIstanbulEventWithRun(newEvent, this.well)

            if (runIndex) {
                this.$store.dispatch('video/skipToFrame', {
                    frame: runIndex,
                    player: this.player
                })
            }
        },
        selectedDaySN(newDay) {
            if (!newDay) {
                return
            }
            const { runIndex } = Utils.getDayWithRun(newDay, this.well)

            if (runIndex >= 0) {
                this.$store.dispatch('video/skipToFrame', {
                    frame: runIndex,
                    player: this.player
                })
            }
        },
        isFertilizationCheck() {
            if (!this.isFertilizationCheck) return

            if (this.pnCountTime && this.pnCount) {
                const runIndex = this.well.runs.findLastIndex(
                    ({ time }) => time <= parseFloat(this.pnCountTime).toFixed(2)
                )
                if (runIndex >= 0) {
                    this.$store.dispatch('video/skipToFrame', {
                        frame: runIndex,
                        player: this.player
                    })
                }
            } else {
                this.$store.dispatch('video/skipToFrame', {
                    frame: 0,
                    player: this.player
                })
            }
        },
        videoSeekMode() {
            if (this.videoSeekMode !== VIDEO_SEEK_MODES.MK_EVENT) {
                this.mkEventUnavailable = false
            }
        }
    },
    mounted() {
        this.player = document.getElementById(this.playerId)
        this.setPlayerListeners()
    },
    methods: {
        setPlayerListeners() {
            if (!this.player) {
                console.log('no player')
                return
            }

            if (this.master) {
                this.$store.commit('timelapse/videoPlaybackReady', false)
            }

            const self = this

            const playerSource = this.$refs.playerSource
            if (playerSource) {
                playerSource.addEventListener('error', function (err) {
                    // workaround for the ERR_CACHE_OPERATION_NOT_SUPPORTED bug
                    self.player.children[0].src = self.videoURL + '?v=' + Math.random()
                    self.player.load()
                })
            }

            this.player.addEventListener('loadedmetadata', function () {
                self.localVideoPlaybackReady = true
                if (self.master) {
                    self.$store.commit('timelapse/videoPlaybackReady', true)
                    if (self.selectedTreatment.status !== TREATMENT_STATUS.INACTIVE)
                        // to avoid overriding for INACTIVE treatments because we skip video to last event on page load
                        self.$store.dispatch('video/seek', { timecode: this.duration })
                }

                if (self.selectedTreatment.status === TREATMENT_STATUS.INACTIVE)
                    self.skipVideoToLastMkEvent()
            })

            if (
                this.currentView === TREATMENT_PAGE_VIEWS.COMPARE_WELLS &&
                this.videoPlaybackReady &&
                !this.isPlayerTimeupdateEventListenerSet
            ) {
                this.addPlayerTimeupdateEventListener()
            }
        },
        addPlayerTimeupdateEventListener() {
            this.player.addEventListener('timeupdate', this.updateTimecodeAndSeekBar)
            this.isPlayerTimeupdateEventListenerSet = true
        },
        updatePlayerCurrentTime() {
            // happens when seeking, not playing
            this.player.currentTime = this.currentTimeCode_s
        },
        updateTimecodeAndSeekBar() {
            // happens in both cases: seeking and playing
            const currentTime = this.player.currentTime
            let currentTimecode = parseFloat(currentTime.toFixed(2))

            if (currentTimecode > 0) {
                currentTimecode -= parseFloat(this.fpsCalibrationTime_s)
            }
            this.timecode = currentTimecode

            if (this.master) {
                this.updateSeekBar()
            }
        },
        updateSeekBar() {
            this.$store.commit('video/seekBar_currentTimeCode_s', this.timecode)
            this.$store.commit('video/currentRunIndex', this.currentRunIndex)
        },
        skipVideoToLastMkEvent() {
            const lastEvent = Utils.getLastMkEventWithImage(this.well)
            if (lastEvent) {
                const { runIndex } = lastEvent
                this.$nextTick(() => {
                    this.$store.dispatch('video/skipToFrame', {
                        frame: runIndex,
                        player: this.player
                    })
                })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.synchronized-well-video {
    position: relative;
    display: flex;
    flex-direction: column;

    .well-time-view {
        position: absolute;
        top: 0.25rem;
        right: 0.25rem;
        padding: 0 0.25rem;
        margin: 0.25rem 0.25rem 0.5rem;
        font-size: 9pt;
        color: $light-3;
        background-color: $dark-4;
        border-radius: $border-radius-sm;
    }

    .well-graphical-events-bar {
        position: absolute;
        bottom: 0;
    }

    .event-unavailable-overlay {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        font-weight: 600;
        background-color: $dark-4;
        opacity: 0.8;
        pointer-events: none;
    }

    .magnifying-area {
        position: absolute;
        right: 0;
        bottom: 5px;
        padding: 5px;
        z-index: 2;

        img {
            width: 12px;
            height: 12px;
        }
    }

    &.scaled {
        transform: scale(2.5);
        z-index: 23;

        .magnifying-area {
            display: none;
        }
    }
}
</style>

<style lang="scss">
.synchronized-well-video {
    .video-spinner {
        min-height: 2rem;
        position: absolute;
        z-index: 999;
        top: calc(50% - 1rem);
        left: calc(50% - 1rem);
    }
    .geri-well-info {
        .well-chamber-number {
            color: $gray4;
        }
    }
}

.well-card {
    // every first in a row
    &:nth-child(4n + 1) {
        .synchronized-well-video {
            transform-origin: center left;
        }
    }
    &:first-child {
        .synchronized-well-video {
            transform-origin: top left;
        }
    }

    // every last in a row
    &:nth-child(4n) {
        .synchronized-well-video {
            transform-origin: center right;
        }
    }
    // last in first row
    &:nth-child(4) {
        .synchronized-well-video {
            transform-origin: top right;
        }
    }

    // mid first line
    &:nth-child(2),
    &:nth-child(3) {
        .synchronized-well-video {
            transform-origin: top;
        }
    }
}
</style>
